import React, { useMemo, useState } from 'react';
import { Car, Percent, TrendingDown } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const makes = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler',
  'Dodge', 'FIAT', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'INFINITI',
  'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Mazda',
  'Mercedes-Benz', 'MINI', 'Nissan', 'Porsche', 'Ram', 'Toyota', 'Volkswagen', 'Volvo'
];

const initialInputs = {
  make: makes[0],
  modelYear: new Date().getFullYear().toString(),
  purchasePrice: '',
  purchaseMileage: '0',
  currentMileage: '',
  yearsOwned: '0',
  avgAnnualMiles: '12000',
  baselineAnnualMiles: '12000',
  yearOneDrop: '20',
  ongoingDrop: '12',
  mileageAdjustment: '150',
  holdYears: '5',
};

const formatCurrency = (value) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safeValue);
};

const formatMiles = (value) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
  return safeValue.toLocaleString();
};

const buildDepreciationSchedule = (inputs) => {
  const purchasePrice = Math.max(parseFloat(inputs.purchasePrice) || 0, 0);
  const purchaseMileage = Math.max(parseFloat(inputs.purchaseMileage) || 0, 0);
  const currentMileageRaw = parseFloat(inputs.currentMileage);
  const yearsOwned = Math.max(parseFloat(inputs.yearsOwned) || 0, 0);
  const avgAnnualMilesInput = Math.max(parseFloat(inputs.avgAnnualMiles) || 0, 0);
  const baselineAnnualMiles = Math.max(parseFloat(inputs.baselineAnnualMiles) || 12000, 0);
  const yearOneDrop = Math.min(Math.max(parseFloat(inputs.yearOneDrop) || 0, 0), 100) / 100;
  const ongoingDrop = Math.min(Math.max(parseFloat(inputs.ongoingDrop) || 0, 0), 100) / 100;
  const mileageAdjustment = Math.max(parseFloat(inputs.mileageAdjustment) || 0, 0);
  const holdYears = Math.max(parseFloat(inputs.holdYears) || 0, 0);

  if (purchasePrice === 0) {
    return {
      schedule: [],
      currentValue: 0,
      totalDepreciation: 0,
      avgAnnualDepreciation: 0,
      projectedValue: 0,
      yearsOwned,
      holdYears,
      purchasePrice,
      qualityScore: '',
      qualityColor: '',
      qualityMsg: '',
    };
  }

  let actualAverageMiles = avgAnnualMilesInput;
  if (!Number.isNaN(currentMileageRaw) && yearsOwned > 0) {
    actualAverageMiles = Math.max((currentMileageRaw - purchaseMileage) / yearsOwned, 0);
  }

  const scheduleYears = Math.max(Math.ceil(Math.max(yearsOwned, holdYears)), 1);
  const schedule = [];
  let baseValue = purchasePrice;
  let prevAdjustedValue = purchasePrice;

  for (let year = 0; year <= scheduleYears; year += 1) {
    const expectedMiles = purchaseMileage + baselineAnnualMiles * year;
    const actualMiles = purchaseMileage + actualAverageMiles * year;
    const mileageDiff = actualMiles - expectedMiles;
    const mileagePenalty = mileageDiff > 0 ? (mileageDiff / 1000) * mileageAdjustment : 0;
    const adjustedValue = Math.max(baseValue - mileagePenalty, 0);
    const annualDepreciation = year === 0 ? 0 : Math.max(prevAdjustedValue - adjustedValue, 0);

    schedule.push({
      year,
      value: adjustedValue,
      depreciation: annualDepreciation,
      actualMiles,
      expectedMiles,
      mileagePenalty,
    });

    prevAdjustedValue = adjustedValue;
    const rate = year === 0 ? yearOneDrop : ongoingDrop;
    baseValue = Math.max(baseValue * (1 - rate), 0);
  }

  const floorYear = Math.min(Math.floor(yearsOwned), schedule.length - 1);
  const ceilYear = Math.min(Math.ceil(yearsOwned), schedule.length - 1);
  const fraction = ceilYear === floorYear ? 0 : (yearsOwned - floorYear) / (ceilYear - floorYear);
  const lower = schedule[floorYear] || schedule[0];
  const upper = schedule[ceilYear] || lower;
  const currentValue = lower.value + (upper.value - lower.value) * fraction;

  const totalDepreciation = Math.max(purchasePrice - currentValue, 0);
  const avgAnnualDepreciation = yearsOwned > 0 ? totalDepreciation / yearsOwned : 0;
  const projectedIndex = Math.min(Math.round(holdYears), schedule.length - 1);
  const projectedValue = schedule[projectedIndex]?.value ?? currentValue;

  const depreciationRatio = purchasePrice > 0 ? totalDepreciation / purchasePrice : 0;

  let qualityScore = '';
  let qualityColor = '';
  let qualityMsg = '';

  if (purchasePrice > 0 && (yearsOwned > 0 || holdYears > 0)) {
    if (depreciationRatio <= 0.35) {
      qualityScore = 'depreciation.quality.great';
      qualityColor = 'bg-green-100 text-green-800';
      qualityMsg = 'depreciation.qualityMsg.great';
    } else if (depreciationRatio >= 0.55) {
      qualityScore = 'depreciation.quality.rapid';
      qualityColor = 'bg-red-100 text-red-800';
      qualityMsg = 'depreciation.qualityMsg.rapid';
    } else {
      qualityScore = 'depreciation.quality.typical';
      qualityColor = 'bg-yellow-100 text-yellow-800';
      qualityMsg = 'depreciation.qualityMsg.typical';
    }
  }

  return {
    schedule,
    currentValue,
    totalDepreciation,
    avgAnnualDepreciation,
    projectedValue,
    yearsOwned,
    holdYears,
    purchasePrice,
    qualityScore,
    qualityColor,
    qualityMsg,
  };
};

const DepreciationCalculator = () => {
  const [inputs, setInputs] = useState(initialInputs);
  const results = useMemo(() => buildDepreciationSchedule(inputs), [inputs]);
  const { t } = useTranslation();

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatMilesWithUnit = (value) => `${formatMiles(value)} ${t('depreciation.table.milesSuffix', 'mi')}`.trim();

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-primary-600" />
          {t('depreciation.title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('depreciation.description')}
        </p>
      </Card>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Car className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('depreciation.section.profile')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t('depreciation.input.make')}</label>
                <select
                  className="w-full rounded border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:bg-dark-900 dark:text-white p-2 bg-white"
                  value={inputs.make}
                  onChange={(e) => handleInputChange('make', e.target.value)}
                >
                  {makes.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </div>
              <CalculatorInput
                label={t('depreciation.input.modelYear')}
                value={inputs.modelYear}
                onChange={(value) => handleInputChange('modelYear', value)}
                type="number"
              />
              <CalculatorInput
                label={t('depreciation.input.purchasePrice')}
                value={inputs.purchasePrice}
                onChange={(value) => handleInputChange('purchasePrice', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('depreciation.input.purchaseMileage')}
                value={inputs.purchaseMileage}
                onChange={(value) => handleInputChange('purchaseMileage', value)}
                suffix={t('depreciation.table.milesSuffix', 'mi')}
              />
              <CalculatorInput
                label={t('depreciation.input.currentMileage')}
                value={inputs.currentMileage}
                onChange={(value) => handleInputChange('currentMileage', value)}
                suffix={t('depreciation.table.milesSuffix', 'mi')}
              />
              <CalculatorInput
                label={t('depreciation.input.yearsOwned')}
                value={inputs.yearsOwned}
                onChange={(value) => handleInputChange('yearsOwned', value)}
                type="number"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Percent className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('depreciation.section.assumptions')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('depreciation.input.avgAnnualMiles')}
                value={inputs.avgAnnualMiles}
                onChange={(value) => handleInputChange('avgAnnualMiles', value)}
                suffix={t('depreciation.table.milesSuffix', 'mi')}
              />
              <CalculatorInput
                label={t('depreciation.input.baselineAnnualMiles')}
                value={inputs.baselineAnnualMiles}
                onChange={(value) => handleInputChange('baselineAnnualMiles', value)}
                suffix={t('depreciation.table.milesSuffix', 'mi')}
              />
              <CalculatorInput
                label={t('depreciation.input.yearOneDrop')}
                value={inputs.yearOneDrop}
                onChange={(value) => handleInputChange('yearOneDrop', value)}
                type="number"
              />
              <CalculatorInput
                label={t('depreciation.input.ongoingDrop')}
                value={inputs.ongoingDrop}
                onChange={(value) => handleInputChange('ongoingDrop', value)}
                type="number"
              />
              <CalculatorInput
                label={t('depreciation.input.mileageAdjustment')}
                value={inputs.mileageAdjustment}
                onChange={(value) => handleInputChange('mileageAdjustment', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('depreciation.input.holdYears')}
                value={inputs.holdYears}
                onChange={(value) => handleInputChange('holdYears', value)}
                type="number"
              />
            </div>
          </Card>
        </div>

        <div className="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-20 lg:self-start">
          <Card className="p-6 space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold uppercase tracking-wide">{t('depreciation.summary.title')}</h3>
              <p className="text-xs text-gray-500 mt-1">{t('depreciation.summary.caption', 'Based on {{years}} years owned', { years: results.yearsOwned })}</p>
            </div>
            {results.qualityScore && (
              <>
                <div className={`rounded px-3 py-2 text-center font-semibold text-sm ${results.qualityColor}`}>
                  {t(results.qualityScore)}
                </div>
                <div className="text-xs text-gray-500 text-center mb-2">{t(results.qualityMsg)}</div>
              </>
            )}
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('depreciation.summary.currentValue')}</div>
              <div className="bg-primary-100 text-primary-900 text-2xl font-bold px-4 py-2 rounded text-center">
                {formatCurrency(results.currentValue)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('depreciation.summary.totalDepreciation')}</div>
              <div className="text-xl font-bold text-center text-primary-700 dark:text-primary-400">
                {formatCurrency(results.totalDepreciation)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('depreciation.summary.avgAnnual')}</div>
              <div className="text-lg font-semibold text-center">
                {formatCurrency(results.avgAnnualDepreciation)}
              </div>
            </div>
            {results.holdYears > results.yearsOwned && (
              <div className="pt-2 border-t border-gray-100 dark:border-dark-700">
                <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('depreciation.summary.projected', 'Projected Value (Year {{year}})', { year: Math.round(results.holdYears) })}</div>
                <div className="text-lg font-semibold text-center">
                  {formatCurrency(results.projectedValue)}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {results.schedule.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('depreciation.table.title')}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
              <thead className="bg-gray-50 dark:bg-dark-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('depreciation.table.year')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('depreciation.table.value')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('depreciation.table.depreciation')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('depreciation.table.actualMiles')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('depreciation.table.expectedMiles')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('depreciation.table.mileagePenalty')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {results.schedule.map((row) => (
                  <tr key={row.year} className="bg-white dark:bg-dark-900">
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{row.year}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-semibold">
                      {formatCurrency(row.value)}
                    </td>
                    <td className="px-4 py-2 text-sm text-primary-700 dark:text-primary-400">
                      {row.year === 0 ? '-' : formatCurrency(row.depreciation)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {formatMilesWithUnit(row.actualMiles)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {formatMilesWithUnit(row.expectedMiles)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {row.mileagePenalty > 0 ? `-${formatCurrency(row.mileagePenalty)}` : '$0'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {results.schedule.length === 0 && (
        <Card className="p-6 text-center text-gray-500 dark:text-gray-400">
          {t('depreciation.empty', 'Enter vehicle details to calculate depreciation.')}
        </Card>
      )}
    </div>
  );
};

export default DepreciationCalculator;
